import * as React from 'react'
import { TouchableOpacity, View } from 'react-native'
import Colors from '../../../constants/Colors'
import { Box, Stack, Text, TextInput, Tile, Tiles } from '../../../components/DesignSystem'
import { useState } from 'react'
import GlobalStyle, {
	formStyle,
	largeTextSize,
	mediumTextSize,
	SMALL_WIDTH,
} from '../../../constants/GlobalStyle'
import { getGiftCardByCode } from '../../../firebase/fbDataRetrieval/getGiftCard'
import { GiftCard } from '../../Checkout/Helpers/validateGiftCard'
import Formatter from '../../../helpers/Formatter'
import {
	GIFT_CARD_CHECK_BALANCE_TEXT,
	GIFT_CARD_CODE_PLACEHOLDER,
	GIFT_CARD_ERRORS,
	GIFT_CARD_INPUT_MAX_LENGTH,
	GIFT_CARD_LENGTH,
} from '../../../constants/checkout/GiftCards'
import { Button } from '../../../components/DesignSystem'
import { useAppSelector } from '../../../state/hooks'

interface ExistingGiftCardMenuProps {
	deviceHasInternet: boolean
}

export default function ExistingGiftCardMenu({ deviceHasInternet }: ExistingGiftCardMenuProps) {
	const { currency } = useAppSelector(state => state.settings)
	const [code, setCode] = useState<string>('')
	const [error, setError] = useState<string>(undefined)
	const [giftCard, setGiftCard] = useState<GiftCard | null>(null)

	const [showCheckBalance, setShowCheckBalance] = useState(false)

	// The 16 char gift card code.
	const cleanedCode = code.replaceAll(' ', '')
	const isValidCode = cleanedCode.length === GIFT_CARD_LENGTH

	const formattedCode = React.useMemo(() => {
		if (!code) return ''
		return Formatter.formatGiftCardCode(code)
	}, [code])

	const handleSubmit = async () => {
		setGiftCard(null)
		setError(undefined)

		if (cleanedCode.length != GIFT_CARD_LENGTH) return setError(GIFT_CARD_ERRORS.invalidCode)

		const data = await getGiftCardByCode(cleanedCode)
		if (!data) return setError(GIFT_CARD_ERRORS.doesNotExist)

		setGiftCard(data)
	}

	return (
		<Stack align="center">
			<Box maxWidth="medium">
				<View style={{ marginVertical: 4 }}>
					<Text style={GlobalStyle.subHeaderText}>Have a gift card?</Text>
					<TouchableOpacity
						style={{ marginTop: 16, marginBottom: 8 }}
						onPress={() => setShowCheckBalance(!showCheckBalance)}
					>
						<Text
							style={{
								textAlign: 'center',
								textDecorationLine: 'underline',
							}}
						>
							Check your gift card balance
						</Text>
					</TouchableOpacity>
					{showCheckBalance && (
						<>
							<Tiles columns={{ mobile: 1, tablet: 4, desktop: 4 }}>
								<Tile width={{ mobile: 1, tablet: 3, desktop: 3 }}>
									<TextInput
										placeholder={GIFT_CARD_CODE_PLACEHOLDER}
										value={formattedCode}
										onChangeText={setCode}
										maxLength={GIFT_CARD_INPUT_MAX_LENGTH}
										width="full"
										size="standard"
										style={{
											marginRight: SMALL_WIDTH ? 0 : 10,
											marginBottom: SMALL_WIDTH ? 10 : 0,
										}}
									/>
								</Tile>
								<Tile width={1}>
									<Button onPress={handleSubmit} disabled={!deviceHasInternet || !isValidCode}>
										{GIFT_CARD_CHECK_BALANCE_TEXT}
									</Button>
								</Tile>
							</Tiles>

							<Text style={{ textAlign: 'center', color: Colors.greyscale[6] }}>
								Gift card code is case-sensitive.
							</Text>
						</>
					)}
					{error && (
						<Text style={{ textAlign: 'center', color: Colors.greyscale[6] }}>{error}</Text>
					)}
					{giftCard && (
						<Text style={{ textAlign: 'center', marginVertical: 10, fontSize: largeTextSize }}>
							Balance: {Formatter.formatCurrency(giftCard.balance / 100, currency)}
						</Text>
					)}
				</View>
			</Box>
		</Stack>
	)
}
